import { match, NO_ELEMENT } from 'utils';
import styles from './styles.scss';
import cn from 'classnames';
import { VariantConfigurator as VariantConfiguratorModel } from 'models/VariantConfigurator';
import { AttributeType } from 'models/AttributeType';
import { ToolTip } from 'components/Shared/ToolTip';
import { ConfiguratorProps, ModalProps } from 'views/ProductPage/configurators/index';
import { Attribute, useConfigurator } from 'views/ProductPage/configurators/useConfigurator';
import { ReactElement } from 'react';
import { MasterAttribute } from 'models/Constraints';
import { ConstraintType } from 'models/ConstraintType';
import { rules } from '../../../rules';
import { IConstraint } from 'models/IConstraint';
import { AttributeSelect } from 'views/ProductPage/configurators/variant/attributeSelect';
import { AttributeNumeric } from 'views/ProductPage/configurators/variant/attributeNumeric';
import { AttributeImage } from 'views/ProductPage/configurators/variant/attributeImage';
import { AttributeFilter } from '../../attributeFilter';

interface OwnProps extends ConfiguratorProps, ModalProps {
  configurator: VariantConfiguratorModel;
}

interface Master {
  isMaster: (id: string) => boolean;
  isMasterSelected: () => boolean;
  hasMasterConstraint: boolean;
}

const isMasterAttributeConstraint = (constraint: IConstraint): constraint is MasterAttribute => {
  return constraint.rule === ConstraintType.Master;
};

export const masterAttribute = ({
  configurator,
  configAttributes
}: Pick<OwnProps, 'configurator'> & { configAttributes: Attribute[] }): Master => {
  const constraint = configurator.constraints.find(isMasterAttributeConstraint);

  if (constraint === undefined) {
    return {
      isMaster: (_: string) => false,
      isMasterSelected: () => false,
      hasMasterConstraint: false
    };
  }

  const isMaster = (id: string) =>
    !!constraint.attributes.find(c => c.toLowerCase() === id.toLowerCase());
  const isMasterSelected = () =>
    constraint.attributes.every(id => {
      const attribute = configAttributes.find(c => c.id.toLowerCase() === id.toLowerCase());
      return Boolean(attribute && attribute.selected);
    });

  return { isMaster, isMasterSelected, hasMasterConstraint: true };
};

const showTooltip = (type: AttributeType, tooltip?: string): tooltip is string =>
  Boolean(tooltip) && type !== AttributeType.Image;

export default function VariantConfigurator(props: OwnProps) {
  const { attributes } = useConfigurator();
  const validate = rules(props);
  const { isMaster, isMasterSelected, hasMasterConstraint } = masterAttribute({
    configurator: props.configurator,
    configAttributes: attributes
  });

  const component = match<Attribute, ReactElement | null>()
    .when(
      a => a.type === AttributeType.Select,
      a => <AttributeSelect {...a} />
    )
    .when(
      a => a.type === AttributeType.NumericInput,
      a => <AttributeNumeric {...{ ...a, validate }} />
    )
    .when(
      a => a.type === AttributeType.Image,
      a => <AttributeImage {...{ ...a, isMaster, hasMasterConstraint }} />
    )
    .when(
      a => a.type === AttributeType.AttributeFilter,
      a => <AttributeFilter {...{ ...a }} />
    )
    .or(NO_ELEMENT)
    .toFunc();

  const Control = (a: Attribute) => {
    const shouldShow = isMaster(a.id) || isMasterSelected();
    if (hasMasterConstraint && !shouldShow) {
      return NO_ELEMENT;
    }

    const control = component(a);

    return (
      <div
        key={a.name}
        className={cn(styles.cell, {
          [styles.two]: a.type !== AttributeType.Image,
          [styles.one]: a.type === AttributeType.Image
        })}
      >
        <label>
          <span className={cn(styles.label, styles.selectLabel)}>{a.name}</span>
          {showTooltip(a.type, a.toolTip) && <ToolTip text={a.toolTip} />}
        </label>
        {control}
      </div>
    );
  };

  return (
    <div className={cn(styles.base, { [styles.mobile]: props.isMobile })}>
      {attributes.map(Control)}
    </div>
  );
}
